import React from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { ClientRecord } from "../../ClientsGrid.utils";
import styles from "./ActionsRenderer.module.css";

interface ActionsRendererProps {
  data: ClientRecord;
  setUploaderShow: (arg: boolean) => void;
  setAddNewWizardShow: (arg: boolean) => void;
  setSendForApprovalBulkApiProgress: (arg: any) => void;
  setSelectedRows: (arg: any) => void;
  deselectRows: any;
  setApprovalSettingModal: (arg: boolean) => void;
  setClientName: (arg: string) => void;
  setClientMasterId: (arg: number) => void;
  setIsClientPublic: (arg: boolean) => void;
  setClientTypeCode: (arg: string) => void;
  setClientId: (arg: string) => void;
  setAuditTrailModalShow: (arg: boolean) => void;
  setApprovalPdfViewerShow: (arg: boolean) => void;
  setClientDetailsPdfUrl: (arg: string) => void;
  sendForApproval: (arg: any[]) => void;
}

const ActionsRenderer: React.FC<ActionsRendererProps> = ({
  data,
  setUploaderShow,
  setAddNewWizardShow,
  setSendForApprovalBulkApiProgress,
  setSelectedRows,
  deselectRows,
  setApprovalSettingModal,
  setClientMasterId,
  setClientName,
  setIsClientPublic,
  setClientTypeCode,
  setClientId,
  setAuditTrailModalShow,
  setApprovalPdfViewerShow,
  setClientDetailsPdfUrl,
  sendForApproval,
}) => {
  if (!data) return null;

  const engage = () => {
    setClientMasterId(data.ClientMasterId);
    setClientName(data["Client Name"]);
    setAddNewWizardShow(true);
  };

  const uploadFiles = () => {
    setClientMasterId(data.ClientMasterId);
    setClientName(data["Client Name"]);
    setIsClientPublic(data.IsPublic.toLowerCase() === "true" ? true : false);
    setUploaderShow(true);
  };

  const sendToApprove = () => {
    setClientMasterId(data.ClientMasterId);
    setClientName(data["Client Name"]);
    sendForApproval(
      [data]
    );
  };

  const openAuditTrail = () => {
    setClientMasterId(data.ClientMasterId);
    setClientId(data.ClientId);
    setClientName(data["Client Name"]);
    setAuditTrailModalShow(true);
  };

  // Open approval PDF viewer - now uses URL directly from grid data
  const openApprovalPdfViewer = () => {
    setClientMasterId(data.ClientMasterId);
    setClientName(data["Client Name"]);
    setClientDetailsPdfUrl(data.ClientDetailsPdfUrl || "");
    setApprovalPdfViewerShow(true);
  };

  const isTaxAcceptanceCompleted =
    data.TaxAcceptanceFormStatus.toLowerCase().includes("completed");
  const isAuditAcceptanceCompleted =
    data.AuditAcceptanceFormStatus.toLowerCase().includes("completed");
  const isAcceptanceCompleted =
    isTaxAcceptanceCompleted || isAuditAcceptanceCompleted;
  const isBackgroundCompleted =
    data.BackgroundCheckStatus.toLowerCase().includes("completed");
  const uploadTooltipMessage =
    data.BackgroundCheckStatus.toLowerCase().includes("rejected")
      ? "Reupload the files"
      : data.BusinessUnit === "tax"
      ? "Not required for Tax"
      : !isAcceptanceCompleted
      ? "Complete the acceptance requirements"
      : "Upload files";

  const uploadButtonDisabled =
    !isAcceptanceCompleted ||
    data.BusinessUnit === "tax" ||
    isBackgroundCompleted;
  const uploadButtonAction = isBackgroundCompleted ? uploadFiles : uploadFiles;
  const isApproved = data["Approval Status"].toLowerCase().includes("approved");
  const approvalTooltipMessage = isApproved
    ? "Client already approved"
    : data["Approval Status"].toLowerCase() === "rejected"
    ? "Resend for approval"
    : "Send for approval";

  const buttonIcon = isApproved ? <Icon.SendSlash /> : <Icon.SendPlus />;
  const approvalButtonAction = isApproved ? undefined : sendToApprove;
  const approvalButtonDisabled = isApproved;
  const isEngagementLetterPending =
    data.EngagementLetterStatus === "pending" ||
    data.EngagementLetterStatus === undefined;

  const engagementButtonAction = isEngagementLetterPending ? engage : undefined;
  const engagementButtonDisabled = !isApproved;
  const engagementTooltipMessage = engagementButtonDisabled
    ? "Client approval is pending."
    : isEngagementLetterPending
    ? "Create Engagement Letter"
    : "Engagement letter already exists. Click to create another.";

  // Check if ClientDetailsPdfUrl exists to determine if button should be shown
  const hasApprovalPdf = !!(data.ClientDetailsPdfUrl && data.ClientDetailsPdfUrl.trim() !== "");

  return (
    <>
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 400 }}
        overlay={
          <Tooltip id="button-tooltip">{engagementTooltipMessage}</Tooltip>
        }
      >
        <span className={styles.tooltipWrapper}>
          <Button
            className={styles.actionButton}
            onClick={engagementButtonAction}
            disabled={engagementButtonDisabled}
          >
            <Icon.FileEarmarkRichtext />
          </Button>
        </span>
      </OverlayTrigger>

      {/* View Approval PDF button - Only render if ClientDetailsPdfUrl exists */}
        <OverlayTrigger
          placement="bottom"
          delay={{ show: 250, hide: 400 }}
          overlay={
            <Tooltip id="button-tooltip">{hasApprovalPdf ? "View Approval Document" : "No Approval Document Available"}</Tooltip>
          }
        >
          <span className={styles.tooltipWrapper}>
            <Button
              className={styles.actionButton}
              onClick={openApprovalPdfViewer}
              disabled={!hasApprovalPdf}
            >
              <Icon.Eye />
            </Button>
          </span>
        </OverlayTrigger>
        
      {/* Send for approval button */}
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 400 }}
        overlay={
          <Tooltip id="button-tooltip">{approvalTooltipMessage}</Tooltip>
        }
      >
        <span className={styles.tooltipWrapper}>
          <Button
            className={styles.actionButton}
            onClick={approvalButtonAction}
            disabled={approvalButtonDisabled}
          >
            {buttonIcon}
          </Button>
        </span>
      </OverlayTrigger>

      {/* Approval Setting button */}
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 400 }}
        overlay={<Tooltip id="button-tooltip">Approval Setting</Tooltip>}
      >
        <span className={styles.tooltipWrapper}>
          <Button
            className={styles.settingActionButton}
            onClick={() => {
              setClientMasterId(data.ClientMasterId || 0);
              setClientName(data["Client Name"]);
              setClientTypeCode(data.ClientTypeCode || "");
              setApprovalSettingModal(true);
            }}
          >
            <Icon.Gear />
          </Button>
        </span>
      </OverlayTrigger>

      <OverlayTrigger
        placement="bottom"
        delay={{ show: 250, hide: 400 }}
        overlay={<Tooltip id="tooltip-audit">View Audit Trail</Tooltip>}
      >
        <span className={styles.tooltipWrapper}>
          <Button className={styles.auditActionButton} onClick={openAuditTrail}>
            <Icon.ClockHistory />
          </Button>
        </span>
      </OverlayTrigger>
    </>
  );
};

export default ActionsRenderer;